import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import { css } from "glamor";
import { format, formatInTimeZone, utcToZonedTime } from "date-fns-tz";
import { useMutation, useQuery } from "@apollo/client";
import * as theme from "../../theme";

import { Button, HollowButton } from "../../components/Button";
import {
  CardContainer,
  ColContainer,
  Container,
} from "../../components/Container";
import { H1, H2 } from "../../components/Typography";
import { Loading, LoadingError } from "../../components/LoadingFork";
import { Table, TBody, THead } from "../../components/Table";
import { Well } from "../../components/Well";
import { withInstallation } from "../../components/InstallationContext";

import LinkTR from "./components/LinkTR";
import RateCardLabel from "./components/RateCardLabel";
import SearchForm from "./components/SearchForm";
import { InlineBookingStatus } from "./components/BookingStatus";
import { listBookings, listGateCharges } from "./queries";
import ReactModal from "react-modal";
import { Input } from "../../components/Input";
import { ValidationErrorMessage } from "../../components/ErrorMessage";
import { sendPayAtGateEmailMutation } from "../Settings/RateCards/queries";
import { CellPill } from "../../components/StatusPill";
import { DRIVO_URL } from "../../lib/drivoApollo";
import {downloadWithAuth, PERMISSIONS} from "../../lib/authManager";
import {fmtDate} from "../../lib/misc";
import { isParklioBayAccessEnabled } from "../../lib/parklioFeature";
import {debounce} from "lodash";
import {PermissionOnly} from "../../components/AdminOnly";

const textBold = css({ fontWeight: "bold", fontSize: 14 });
const textGray = css({ color: theme.gray40, fontSize: 14 });

/* A booking "expects a bay" (so a missing one shows "Assign bay" rather than "—")
 * when its card opts into Parklio bay assignment (rateCard.parklioBayAssignment) —
 * the same per-card signal the New Booking form gates the bay picker on. */

/* The Upcoming/Active badge and the "Assign bay" prompt only make sense for a live
 * booking — terminal/past bookings just show the bay label (or "—"). */
const isLive = (status) => status === "PENDING_ENTRY" || status === "CURRENT";
const addPlateButton = css({
  marginTop: 6,
  fontSize: 12,
  fontWeight: "inherit",
  height: "unset",
  padding: "2px 8px",
});
const rowStyle = css({
  "> td": {
    verticalAlign: "middle",
    height: 60,
    borderBottom: `1px solid ${theme.gray10}`,
    transition: "all .2s linear",
    textAlign: "center",
  },
  "> td:first-child": { paddingLeft: 10 },
  "> td:last-child": { paddingRight: 10 },
  ":hover > td": {
    background: theme.gray10,
    cursor: "pointer",
  },
});

const extraRowStyle = css({
  "> td": {
    verticalAlign: "middle",
    height: 60,
    borderBottom: `1px solid ${theme.gray10}`,
    transition: "all .2s linear",
    textAlign: "center",
    background: theme.barelyYellow,
  },
  "> td:first-child": { paddingLeft: 10 },
  "> td:last-child": { paddingRight: 10 },
  ":hover > td": {
    background: theme.gray10,
    cursor: "pointer",
  },
});

const GATE_CHARGES_PAGE_SIZE = 20;
const INITIAL_GATE_PAGING = { offset: GATE_CHARGES_PAGE_SIZE, hasMore: true };

function appendGateCharges(previous, { fetchMoreResult }) {
  const existing = previous?.getGateChargesForInstallation || [];
  const incoming = fetchMoreResult?.getGateChargesForInstallation || [];
  const existingIds = new Set(existing.map((charge) => charge.id));

  return {
    ...previous,
    getGateChargesForInstallation: [
      ...existing,
      ...incoming.filter((charge) => !existingIds.has(charge.id)),
    ],
  };
}

export async function EmailButtonClick(booking, setFormOpen, setSelectBooking) {
  await setSelectBooking(booking);
  setFormOpen(true);
}

function PayAtGateRow({ booking, setFormOpen, setSelectBooking, installationTimezone, parklioEnabled }) {

  return (
    <LinkTR className={rowStyle}>
      <td className={textBold}>{booking.plate}</td>
      <HollowButton
        onClick={() => EmailButtonClick(booking, setFormOpen, setSelectBooking)}
        className={css({
          marginTop: "20px",
          fontSize: "14px",
          fontWeight: "inherit",
          height: "unset",
        })}
      >
        Email
      </HollowButton>
      <td className={textGray}>
        {formatInTimeZone(new Date(Number(booking.createdAt)), installationTimezone, "yyyy-MM-dd")}
      </td>
      <td className={textGray}>-</td>
      <td className={textGray}>
        Pay @ Gate
        {booking.gateName && <div>{booking.gateName}</div>}
      </td>
      {parklioEnabled && <td className={textGray}>-</td>}
      <td>${booking.amount / 100}</td>
    </LinkTR>
  );
}

export function GetCleanIntervalForBooking(bookingInterval, timezone) {
  if (bookingInterval == null) {
    return "";
  }

  let start = bookingInterval.split("/")[0];
  let end = bookingInterval.split("/")[1];
  // 2021-05-13T07:29:00.643+10:00/2021-05-13T08:42:37.081+10:00
  let startDate = utcToZonedTime(new Date(start), timezone);
  let endDate = utcToZonedTime(new Date(end), timezone);

  return `${format(startDate, "h:maaa d MMM", { timeZone: timezone })} to 
    ${format(endDate, "h:maaa d MMM", { timeZone: timezone })}`;
}

function BookingList({ installation, facility, history, match }) {
  const parklioEnabled = isParklioBayAccessEnabled(facility);
  function BookingRow({ href, booking }) {
    /* No-plate (hire car) booking — the QR pass is its only gate credential
       until the front desk adds the real plate. */
    const isUnknownPlate = booking.plate === "UNKNOWN";
    return (
      <LinkTR
        className={
          booking.bookingExtras && booking.bookingExtras.length > 0
            ? extraRowStyle
            : rowStyle
        }
        href={href}
      >
        <td className={textBold}>
          {isUnknownPlate ? (
            <div>
              <CellPill color="#8B5CF6" width="100px" size="tableCell">
                UNKNOWN · QR
              </CellPill>
              <HollowButton
                onClick={(event) => {
                  event.stopPropagation();
                  history.push(`${match.url}/create?bookingId=${booking.id}`);
                }}
                className={addPlateButton}
              >
                Add plate
              </HollowButton>
            </div>
          ) : (
            <CellPill color="#37B534" width="100px" size="tableCell">
              {booking.plate}
            </CellPill>
          )}
        </td>
        <td>
          {booking.guestName && (
            <CellPill
              className={textBold}
              width="100px"
              color="#FFA800"
              size="tableCell"
            >
              {booking.guestName}
            </CellPill>
          )}
        </td>
        <td className={textGray}>
          <CellPill width="117px" color="#FF4773" size="tableCell">
            {" "}
            {fmtDate(booking.startDay, installation.timeZoneName, "yyyy/MM/dd")}
          </CellPill>
        </td>
        <td className={textGray}>
          <CellPill width="117px" color="#45B5F4" size="tableCell">
            {fmtDate(booking.endDay, installation.timeZoneName, "yyyy/MM/dd")}
          </CellPill>
        </td>
        <td className={textGray}>
          <CellPill width="237px" color="#08515B" size="tableCell">
            {" "}
            <RateCardLabel rateCard={booking.rateCard} />
          </CellPill>
        </td>
        {parklioEnabled && (
          <td className={textGray}>
            {booking.parklioBayLabel ? (
              <div>
                <div className={textBold}>{booking.parklioBayLabel}</div>
                {isLive(booking.status) && (
                  <div
                    className={css({
                      fontSize: 12,
                      marginTop: 2,
                      color:
                        new Date(booking.startDay) > new Date()
                          ? theme.gray50
                          : "#3EAC4D",
                    })}
                  >
                    {new Date(booking.startDay) > new Date()
                      ? "Upcoming"
                      : "Active"}
                  </div>
                )}
              </div>
            ) : isLive(booking.status) && booking.rateCard?.parklioBayAssignment === true ? (
              <span
                className={css({
                  color: "#FFA800",
                  fontWeight: 600,
                  fontSize: 13,
                })}
              >
                Assign bay
              </span>
            ) : (
              "—"
            )}
          </td>
        )}
        <td>
          <InlineBookingStatus status={booking.status} />
        </td>
      </LinkTR>
    );
  }

  const [statusFilter, setStatusFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [sendPayAtGateEmail, { error: sendPayAtGateEmailError }] = useMutation(sendPayAtGateEmailMutation);
  const {
    data = {},
    loading,
    error,
    refetch,
    fetchMore,
  } = useQuery(listBookings, {
    variables: {
      offset: 0,
      status: statusFilter || undefined,
      search: searchTerm || undefined,
      parklioEnabled,
    },
  });

  const {
    data: gateData = {},
    loading: gateLoading,
    refetch: refetchGate,
    fetchMore: fetchMoreGate,
  } = useQuery(
    listGateCharges,
    {
      fetchPolicy: "no-cache",
      variables: {
        input: {
          installationId: installation.id,
          offset: 0,
          search: searchTerm || "",
        },
      },
    }
  );

  const gateGenerationRef = useRef(0);
  const [gatePaging, setGatePaging] = useState(INITIAL_GATE_PAGING);
  const [gateRefreshing, setGateRefreshing] = useState(false);

  const resetGatePaging = useCallback(() => {
    gateGenerationRef.current += 1;
    setGatePaging(INITIAL_GATE_PAGING);
    setGateRefreshing(false);
  }, []);

  useEffect(() => {
    resetGatePaging();
  }, [installation.id, searchTerm, resetGatePaging]);

  const bookings = data.listBookings?.items || [];
  const bookingsCount = data.listBookings?.count || 0;
  const moreBookingsToLoad = bookings.length < bookingsCount;

  const gateBookings = gateData.getGateChargesForInstallation || [];
  const moreGateChargesToLoad =
    statusFilter === "" &&
    !gateRefreshing &&
    gatePaging.hasMore &&
    gateBookings.length >= GATE_CHARGES_PAGE_SIZE;

  const moreToLoad = moreBookingsToLoad || moreGateChargesToLoad;

  function loadMoreGateCharges() {
    const requestGeneration = gateGenerationRef.current;
    const requestOffset = gatePaging.offset;

    fetchMoreGate({
      variables: {
        input: {
          installationId: installation.id,
          offset: requestOffset,
          search: searchTerm || "",
        },
      },
      updateQuery: (previous, options) =>
        requestGeneration === gateGenerationRef.current
          ? appendGateCharges(previous, options)
          : previous,
    }).then((result) => {
      if (requestGeneration !== gateGenerationRef.current) {
        return;
      }
      const pageSize = result?.data?.getGateChargesForInstallation?.length || 0;
      const nextOffset = requestOffset + GATE_CHARGES_PAGE_SIZE;
      setGatePaging((current) =>
        nextOffset < current.offset
          ? current
          : {
              offset: nextOffset,
              hasMore: pageSize >= GATE_CHARGES_PAGE_SIZE,
            }
      );
    });
  }

  function loadMore() {
    if (moreBookingsToLoad) {
      fetchMore({
        variables: {
          offset: bookings.length,
          parklioEnabled,
        },
      });
    }
    if (moreGateChargesToLoad) {
      loadMoreGateCharges();
    }
  }

  function refreshList() {
    refetch();
    resetGatePaging();
    const refreshGeneration = gateGenerationRef.current;
    setGateRefreshing(true);
    refetchGate().finally(() => {
      if (refreshGeneration === gateGenerationRef.current) {
        setGateRefreshing(false);
      }
    });
  }

  const debouncedSearch = useMemo(
      () =>
          debounce((value, status) => {
            setSearchTerm(value);
            setStatusFilter(status);
          }, 300),
      []
  );

  useEffect(() => {
    return () => debouncedSearch.cancel();
  }, [debouncedSearch]);

  function onSearch(e) {
    debouncedSearch(e.searchTerm, e.statusFilter);
  }

  async function sendMail() {
    if (name === "") {
      setModalNameError("Please Fill Name ..");
    } else if (email === "") {
      setModalError("Please Fill Email ..");
    } else {
      await sendPayAtGateEmail({
        variables: {
          input: {
            name: name,
            email: email,
            chargeId: selectBooking.id,
          },
        },
      });
      closeModal();
    }
  }

  function closeModal() {
    setModalError("");
    setModalNameError("");
    setFormOpen(false);
    setName("");
    setEmail("");
  }

  function ChangeValue(e, stateValue) {
    setModalError("");
    setModalNameError("");
    stateValue(e);
  }

  const [isFormOpen, setFormOpen] = useState(false);
  const [selectBooking, setSelectBooking] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [modalNameError, setModalNameError] = useState("");
  const [modalError, setModalError] = useState("");

  useEffect(() => {
    data && refetch()
  }, [installation, facility, history, match])

  const handleDownload = async (e) => {
    e.preventDefault();
    if (!facility?.id) {
      window.alert("Download Extra Report is unavailable until a facility is selected.");
      return;
    }
    const filename = `extras_${installation.brandingKey}.csv`;
    await downloadWithAuth({
      url: `${DRIVO_URL}/extras-report/${facility.id}/download`,
      filename,
    });
  };

  return (
    <div>
      <CardContainer>
        <Container size="large" spaceBelow="medium"  >
          <H1>Bookings </H1>
          <div
            style={{
              display: "flex",
              gap: "20px",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <SearchForm onUpdate={onSearch} />
            <PermissionOnly permissions={PERMISSIONS.REPORTS_EXPORT}>
              <Button
                onClick={handleDownload}
                spaceAbove="medium"
                spaceBelow="medium"
                className={css({
                  marginTop: 0,
                  marginBottom: 16,
                })}
              >
                Download Extra Report
              </Button>
            </PermissionOnly>
          </div>
        </Container>
        <Container size="large">
          {error ? <LoadingError error={error} /> : null}
          {bookings.length === 0 && gateBookings.length === 0 ? (
            <Well>
              <div>
                <H2 spaceAbove="large" spaceBelow="large">
                  {(loading || gateLoading) ? <Loading /> : "No bookings available"}
                </H2>
              </div>
            </Well>
          ) : (
            <div>
              <Table>
                <THead>
                  <tr>
                    <th style={{ textAlign: "center" }}>Licence plate</th>
                    <th style={{ textAlign: "center" }}>Customer</th>
                    <th style={{ textAlign: "center" }}>Start Day</th>
                    <th style={{ textAlign: "center" }}>End Day</th>
                    <th style={{ textAlign: "center" }}>Guest card</th>
                    {parklioEnabled && (
                      <th style={{ textAlign: "center" }}>Bay</th>
                    )}
                    <th style={{ textAlign: "center" }}>Status</th>
                  </tr>
                </THead>
                <TBody>
                  {bookings.map((booking) => (
                    <BookingRow
                      key={booking.id}
                      booking={booking}
                      href={`${match.url}/${booking.id}`}
                    />
                  ))}
                  {statusFilter === "" &&
                    gateBookings.map((booking) => (
                      <PayAtGateRow
                        key={booking.id}
                        booking={booking}
                        setFormOpen={setFormOpen}
                        setSelectBooking={setSelectBooking}
                        installationTimezone={installation.timeZoneName}
                        parklioEnabled={parklioEnabled}
                      />
                    ))}
                </TBody>
              </Table>
              <div
                className={css({
                  textAlign: "center",
                })}
              >
                {moreToLoad ? (
                  <Button
                    onClick={loadMore}
                    spaceAbove="medium"
                    spaceBelow="medium"
                  >
                    Load more results
                  </Button>
                ) : null}
                <HollowButton
                  className={css({ marginLeft: 20 })}
                  onClick={refreshList}
                  spaceAbove="medium"
                  spaceBelow="medium"
                >
                  Refresh
                </HollowButton>
              </div>
            </div>
          )}

          <ReactModal
            isOpen={isFormOpen}
            onRequestClose={closeModal}
            className={css({
              display: "block",
              background: "white",
              margin: 10,
              padding: 20,
              borderRadius: 6,
              outline: "none",
              minWidth: 400,
            }).toString()}
            overlayClassName={css({
              backgroundColor: "rgba(0,0,0,0.2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "fixed",
              zIndex: 100000,
              top: 0,
              right: 0,
              bottom: 0,
              left: 0,
            }).toString()}
          >
            {modalError && <ValidationErrorMessage error={modalError} />}
            {modalNameError && (
              <ValidationErrorMessage error={modalNameError} />
            )}
            <h1 className={css({ textAlign: "left" })}>
              {selectBooking.plate}
            </h1>
            <ColContainer>
              <h3 className={css({ textAlign: "left" })}>
                {GetCleanIntervalForBooking(
                  selectBooking.interval,
                  installation.timeZoneName
                )}
              </h3>
              <h3 className={css({ textAlign: "right" })}>
                ${selectBooking.amount / 100}
              </h3>
            </ColContainer>
            <ColContainer>
              <Input
                required
                error={modalNameError}
                label="Name"
                value={name}
                onValueChange={(e) => ChangeValue(e, setName)}
              />
              <Input
                required
                error={modalError}
                label="Email"
                value={email}
                onValueChange={(e) => ChangeValue(e, setEmail)}
              />
            </ColContainer>
            <HollowButton className={css({ marginTop: 30 })} onClick={sendMail}>
              Send Email
            </HollowButton>
          </ReactModal>
        </Container>
      </CardContainer>
    </div>
  );
}

export default withInstallation(BookingList);
